"use client";

import { useEffect, useId, useState } from "react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  shouldShowAiImageBadge,
  type AiImageLabelInput,
} from "@pulse/shared";
import { BottomSheet } from "@/components/BottomSheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type AiImageBadgeProps = AiImageLabelInput & {
  className?: string;
  /** `xs` = list-row thumbs (~48–64px); `sm` = compact covers; `md` = cards/heroes. */
  size?: "xs" | "sm" | "md";
  /**
   * Corner on the cover. Default bottom-right — top is reserved for
   * New/Popular/Top Pick and save controls.
   */
  corner?: "bottom-right" | "bottom-left";
};

function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setCoarse(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return coarse;
}

/**
 * Subtle EU AI Act Art. 50 attribution mark for AI venue/event covers.
 * Parent must be `position: relative` (card media / hero / gallery wrap).
 */
export function AiImageBadge({
  isAiGenerated,
  className,
  size = "md",
  corner = "bottom-right",
}: AiImageBadgeProps) {
  const { t } = useTranslation("consumer");
  const coarse = useCoarsePointer();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tipId = useId();

  if (!shouldShowAiImageBadge({ isAiGenerated })) return null;

  const aria = t("media.aiImageBadgeAria");
  const hint = t("media.aiImageHint");
  const title = t("media.aiImageTitle");

  const button = (
    <button
      type="button"
      className={cn(
        "ai-image-badge",
        size === "sm" && "ai-image-badge--sm",
        size === "xs" && "ai-image-badge--xs",
        corner === "bottom-left" && "ai-image-badge--bl",
        className,
      )}
      aria-label={aria}
      aria-describedby={coarse ? undefined : tipId}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (coarse) {
          setSheetOpen(true);
          return;
        }
        setTooltipOpen((v) => !v);
      }}
    >
      <Sparkles aria-hidden className="ai-image-badge__icon" strokeWidth={2} />
    </button>
  );

  return (
    <>
      {coarse ? (
        button
      ) : (
        <TooltipProvider delayDuration={200}>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent
              id={tipId}
              side="top"
              align="end"
              className="ai-image-badge__tooltip max-w-[220px] text-left leading-snug"
              onClick={(e) => e.stopPropagation()}
            >
              {hint}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <BottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={title}
        compact
        layer={80}
      >
        <p className="text-sm text-muted-foreground leading-relaxed">{hint}</p>
      </BottomSheet>
    </>
  );
}
